/**
 * ALARM BAHAN (TUJUAN_ETAMSIOS, K150; langkah 18 sisi L) — stok kain tidak cukup untuk pesanan antrean dalam
 * waktu datang bahan cabang (Samarinda 10 hari, Surabaya 3 hari). Dilihat Admin Produksi + Kepala Cabang
 * (Owner semua region). Kain yang belum diisi "set per satuan" di Master Bahan tidak ditebak.
 *
 * Pola daftar seragam (28 Sep 2026): kedua tabel memakai `useDaftar` mode KLIEN — sengaja. Isinya
 * LAPORAN yang dihitung server UTUH dalam satu panggilan (kain × cabang; bahan terpakai × cabang),
 * tidak dipotong halaman di server, jadi cari/urut/halaman di browser bekerja atas SELURUH hasil.
 * Dua daftar di satu layar → awalan alamat `al_` dan `rm_`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { toast } from "sonner";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const teksStatus = (h) => (!h.bisa_dihitung ? "belum bisa dihitung" : h.alarm ? `kurang ${h.kurang_satuan}` : "cukup");
const KOLOM_ALARM = [
  { kunci: "region", judul: "Wilayah" }, { kunci: "nama", judul: "Kain" }, { kunci: "kode", judul: "Kode" },
  { kunci: "fa_antrean", judul: "FA antrean" }, { kunci: "butuh_satuan", judul: "Butuh" },
  { kunci: "stok_satuan", judul: "Stok" }, { kunci: "satuan", judul: "Satuan" },
  // urut "Status": kurang dulu, lalu belum bisa dihitung, lalu cukup
  { kunci: "status", judul: "Status", ambil: teksStatus, nilai: (h) => (!h.bisa_dihitung ? 1 : h.alarm ? 0 : 2) },
];
const KOLOM_RAMALAN = [
  { kunci: "region", judul: "Wilayah" }, { kunci: "nama", judul: "Bahan", ambil: (h) => h.nama || h.kode },
  { kunci: "pemakaian_harian", judul: "Pemakaian/hari" }, { kunci: "stok", judul: "Stok" },
  { kunci: "stok_cukup_hari", judul: "Cukup (hari)" }, { kunci: "saran_beli", judul: "Saran beli" },
  { kunci: "satuan", judul: "Satuan" },
];

export default function AlarmBahan() {
  const [data, setData] = useState(null);
  const muat = useCallback(async () => {
    try { const r = await api.get("/inventory/alarm-bahan"); setData(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Alarm bahan gagal dimuat")); }
  }, []);
  useEffect(() => { muat(); }, [muat]);
  const items = data?.items || [];
  const daftarAlarm = useDaftar(items, {
    awalan: "al_", kunciCari: ["region", "nama", "kode"], kolom: KOLOM_ALARM,
    urutAwal: { kunci: "status", arah: "asc" },
  });
  // Ramalan kebutuhan bahan (inventaris): pemakaian rata-rata harian dari pengeluaran bahan + saran beli.
  const [ramalan, setRamalan] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/inventory/ramalan-bahan"); setRamalan(r.data); }
      catch (e) { toast.error(apiErrorMessage(e, "Ramalan bahan gagal dimuat")); setRamalan({ items: [] }); }
    })();
  }, []);
  const daftarRamalan = useDaftar(ramalan?.items || [], {
    awalan: "rm_", kunciCari: ["region", "nama", "kode"], kolom: KOLOM_RAMALAN,
  });

  return (
    <div className="max-w-5xl space-y-4" data-testid="alarm-bahan-page">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Alarm Bahan</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Kebutuhan kain pesanan antrean dibanding stok cabang, dalam waktu datang bahan (Samarinda 10 hari, Surabaya 3 hari).
            Isi "potong atasan / celana per satuan" di <Link to="/inventory/materials" className="underline">Master Bahan</Link> agar kain bisa dihitung.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={muat}>Muat ulang</Button>
      </div>
      <PalangDaftar daftar={daftarAlarm} placeholder="Cari cabang / kain" testId="alarm-palang"
                    ekspor={{ nama: "alarm_bahan", kolom: KOLOM_ALARM }} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <ThUrut daftar={daftarAlarm} kunci="region" className="px-3 py-2">Wilayah</ThUrut>
              <ThUrut daftar={daftarAlarm} kunci="nama" className="px-3 py-2">Kain</ThUrut>
              <ThUrut daftar={daftarAlarm} kunci="fa_antrean" className="px-3 py-2" align="right">FA antrean</ThUrut>
              <th className="px-3 py-2 text-right">Lembar (atasan · celana)</th>
              <ThUrut daftar={daftarAlarm} kunci="butuh_satuan" className="px-3 py-2" align="right">Butuh</ThUrut>
              <ThUrut daftar={daftarAlarm} kunci="stok_satuan" className="px-3 py-2" align="right">Stok</ThUrut>
              <ThUrut daftar={daftarAlarm} kunci="status" className="px-3 py-2">Status</ThUrut>
            </tr>
          </thead>
          <tbody>
            {data && daftarAlarm.tampil.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                {items.length ? "Tidak ada yang cocok." : "Tidak ada kebutuhan kain di antrean."}
              </td></tr>
            )}
            {daftarAlarm.tampil.map((h) => (
              <tr key={`${h.region_id}-${h.material_id}`} className="border-t">
                <td className="px-3 py-2">{h.region}</td>
                <td className="px-3 py-2">{h.nama}<div className="font-mono text-xs text-muted-foreground">{h.kode}</div></td>
                <td className="px-3 py-2 text-right tabular-nums">{h.fa_antrean}</td>
                <td className="px-3 py-2 text-right tabular-nums">{h.lembar_atasan} · {h.lembar_celana}</td>
                <td className="px-3 py-2 text-right tabular-nums">{h.butuh_satuan ?? "—"} {h.satuan}</td>
                <td className="px-3 py-2 text-right tabular-nums">{h.stok_satuan} {h.satuan}</td>
                <td className="px-3 py-2">
                  {!h.bisa_dihitung ? <StatusBadge tone="warning">belum bisa dihitung ({(h.belum_bisa_dihitung || []).join(", ")})</StatusBadge>
                    : h.alarm ? <StatusBadge tone="danger">kurang {h.kurang_satuan}</StatusBadge>
                      : <StatusBadge tone="success">cukup</StatusBadge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftarAlarm} testId="alarm-paginasi" />
      </Card>

      <Card className="overflow-x-auto" data-testid="ramalan-bahan">
        <div className="px-3 py-2 text-sm font-medium">
          Ramalan pemakaian ({ramalan?.jendela_hari ?? 90} hari terakhir) — saran beli = pemakaian harian × waktu datang bahan − stok
        </div>
        {ramalan === null ? <div className="px-3 pb-3 text-sm text-muted-foreground">Memuat…</div>
          : (ramalan.items || []).length === 0 ? (
            <div className="px-3 pb-3 text-sm text-muted-foreground">Belum ada pengeluaran bahan dalam jendela ini.</div>
          ) : (
          <>
            <div className="px-3">
              <PalangDaftar daftar={daftarRamalan} placeholder="Cari wilayah / bahan" testId="ramalan-palang"
                            ekspor={{ nama: "ramalan_bahan", kolom: KOLOM_RAMALAN }} />
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <ThUrut daftar={daftarRamalan} kunci="region" className="px-3 py-2">Wilayah</ThUrut>
                  <ThUrut daftar={daftarRamalan} kunci="nama" className="px-3 py-2">Bahan</ThUrut>
                  <ThUrut daftar={daftarRamalan} kunci="pemakaian_harian" className="px-3 py-2" align="right">Pemakaian/hari</ThUrut>
                  <ThUrut daftar={daftarRamalan} kunci="stok" className="px-3 py-2" align="right">Stok</ThUrut>
                  <ThUrut daftar={daftarRamalan} kunci="stok_cukup_hari" className="px-3 py-2" align="right">Cukup (hari)</ThUrut>
                  <ThUrut daftar={daftarRamalan} kunci="saran_beli" className="px-3 py-2" align="right">Saran beli</ThUrut>
                </tr>
              </thead>
              <tbody>
                {daftarRamalan.tampil.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">Tidak ada yang cocok.</td></tr>
                )}
                {daftarRamalan.tampil.map((h) => (
                  <tr key={`${h.region_id}-${h.material_id}`} className="border-t">
                    <td className="px-3 py-2">{h.region}</td>
                    <td className="px-3 py-2">{h.nama || h.kode}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.pemakaian_harian} {h.satuan}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.stok} {h.satuan}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.stok_cukup_hari ?? "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {h.perlu_beli ? <StatusBadge tone="warning">{h.saran_beli} {h.satuan}</StatusBadge> : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftarRamalan} testId="ramalan-paginasi" />
          </>
        )}
      </Card>
    </div>
  );
}
